import { Component } from "react"

/* Redux */
import { connect } from "react-redux"

/* Components */
import { InnerLayout, Pagination, Avatar } from "../../components"

/* Widgets */
import { Filter, CensorForm } from "../../widgets/Message"
import { PlayerView } from "../../widgets/Player"

/* REST API */
import { list, censorList, censorUpdate, censorDelete } from "../../api/Message"

/* Helpers */
import { withRouter, utils } from "../../helpers"

/* Icons */
import { SearchIcon, CloseIcon, FilterIcon, ChatIcon, CopyIcon, PlayerIcon, TableIcon, StudioIcon, OperatorIcon, DealerIcon, LayersIcon, ClockIcon, HashIcon, VipIcon, ShieldIcon, BookIcon, PlusIcon, EditIcon, TrashIcon, PowerIcon, AdminIcon, CheckIcon } from "../../widgets/Message/MessageIcons"

/* Utils */
import { getGame, getName, getAvatar, getAvaIndex, formatCount, formatDay, formatTime, formatAgo } from "../../widgets/Message/MessageUtils"

/* Translate */
import { t } from "../../translates/translate"

/* Sweet alert */
import Swal from "../../helpers/alert"

/* Moment */
import moment from "moment"


/* PAGE SIZE — THE SAME NUMBER THE CONTROLLER USES */
const SIZE = 20

/* DELAY BEFORE THE SEARCH REQUEST LEAVES */
const DELAY = 400

/* THE TAB OF THE DICTIONARY — IT HOLDS NO MESSAGES, SO IT HAS NO PAGES AND NO FILTER OF ITS OWN */
const DICTIONARY = "dictionary"

/* EMPTY ANSWER — THE SAME SHAPE THE CONTROLLER SENDS */
const EMPTY = {
    counts: { all: 0, censored: 0 },
    stats: { players: 0, tables: 0, dealers: 0, last: null },
}


/* Page Messages */
class Messages extends Component {

    constructor() {
        super()

        this.state = {
            data: [],
            status: "loading",
            count: 0,
            tab: "all",
            search: "",
            operators: [],
            tables: [],
            selected: "all",
            start: null,
            end: null,
            testers: false,
            counts: EMPTY.counts,
            stats: EMPTY.stats,

            /* Dictionary Of The Censor */
            censor: [],
            censorStatus: "loading",
            censorStats: { total: 0, active: 0 },
        }
    }

    componentDidMount = () => {
        window.scrollTo(0, 0)
        this.load()

        /* The dictionary is small and its number stands on the tab, so it is read right away */
        this.loadCensor()
    }

    componentDidUpdate = (prevProps) => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page — a new page starts from the top with the loader */
        if (prevProps.params.page !== page) {
            window.scrollTo(0, 0)
            this.load(true)
        }

    }

    componentWillUnmount = () => {
        clearTimeout(this._timer)
    }

    /* Get Page */
    getPage = () => {

        /* Fields */
        const { params } = this.props
        const { page } = params

        /* Check Page */
        if (Number.isInteger(parseInt(page))) {
            return parseInt(page)
        }

        return 1
    }

    /* Load Action */
    load = (reload = false) => {

        /* Fields */
        const { tab, search, operators, tables, start, end, testers } = this.state
        const page = this.getPage()

        /* Check Tab — the dictionary is not a list of messages */
        if (tab === DICTIONARY) {
            this.loadCensor(reload)
            return
        }

        /* Check Reload */
        if (reload) {
            this.setState({ status: "loading" })
        }

        /* Send to REST API */
        list({
            page,
            type: tab,
            search,
            operators: operators.map((item) => item.id).join(","),
            tables: tables.map((item) => item.id).join(","),
            start: start ? moment(start).format("YYYY-MM-DD HH:mm:ss") : "",
            end: end ? moment(end).format("YYYY-MM-DD HH:mm:ss") : "",
            testers,
        }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    data: response.data.list,
                    count: response.data.count,
                    counts: response.data.counts ? response.data.counts : EMPTY.counts,
                    stats: response.data.stats ? response.data.stats : EMPTY.stats,
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ status: "permission", data: [], count: 0, ...EMPTY })
            }
            else if (response.status === 498) {
                this.setState({ status: "key", data: [], count: 0, ...EMPTY })
            }
            else {
                this.setState({ status: "error", data: [], count: 0, ...EMPTY })
            }
        }).catch(() => {
            this.setState({ status: "network", data: [], count: 0, ...EMPTY })
        })

    }

    /* Load The Dictionary — the whole list at once, it is measured in words and not in pages */
    loadCensor = (reload = false) => {

        /* Fields */
        const { tab, search } = this.state

        /* Check Reload */
        if (reload) {
            this.setState({ censorStatus: "loading" })
        }

        /* Send to REST API — the search box belongs to the open tab only */
        censorList({ search: tab === DICTIONARY ? search : "" }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    censorStatus: "ok",
                    censor: response.data.list,
                    censorStats: response.data.stats ? response.data.stats : { total: 0, active: 0 },
                })
            }
            else if (response.status === 401 || response.status === 403) {
                this.setState({ censorStatus: "permission", censor: [] })
            }
            else if (response.status === 498) {
                this.setState({ censorStatus: "key", censor: [] })
            }
            else {
                this.setState({ censorStatus: "error", censor: [] })
            }
        }).catch(() => {
            this.setState({ censorStatus: "network", censor: [] })
        })

    }

    /* Change Tab — every tab is a list of its own, so the search box and the page start over */
    changeTab = (tab) => {

        /* Check Tab */
        if (this.state.tab === tab) {
            return
        }

        /* The dictionary needs no page of the address bar */
        if (tab === DICTIONARY) {
            this.setState({ tab, search: "", censorStatus: "loading" }, () => this.loadCensor())
            return
        }

        this.setState({ tab, search: "", status: "loading" }, () => this.apply())
    }

    /* Change Search — the request leaves only after the typing stops */
    changeSearch = (search) => {

        this.setState({ search })

        clearTimeout(this._timer)
        this._timer = setTimeout(() => this.apply(), DELAY)
    }

    /* Apply Filter */
    applyFilter = ({ selected, start, end, operators, tables, testers }) => {
        this.setState({ selected, start, end, operators, tables, testers }, () => this.apply())
    }

    /* Apply Action — every filter restarts from the first page */
    apply = () => {

        /* Fields */
        const { tab } = this.state
        const { navigate } = this.props

        /* Check Tab */
        if (tab === DICTIONARY) {
            this.loadCensor()
            return
        }

        /* Check Page */
        if (this.getPage() !== 1) {
            navigate("/message/1")
            return
        }

        this.load()
    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Switch A Word On Or Off — a word that is only being tried out is switched off, not deleted */
    toggleWord = (item) => {

        /* Fields */
        const isActive = parseInt(item.isActive) === 1 ? 0 : 1

        /* Send to REST API */
        censorUpdate({ id: item.id, isActive }).then((response) => {
            if (response.status === 200) {
                Swal.fire(utils.notification(isActive === 1 ? t("censor.switchedOn") : t("censor.switchedOff"), "success"))
                this.loadCensor()
            }
            else {
                Swal.fire(utils.notification(this.message(response), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        })

    }

    /* Remove A Word From The Dictionary */
    deleteWord = (item) => {

        Swal.fire({
            title: t("censor.deleteConfirm"),
            text: item.word,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#f44336",
            reverseButtons: true,
            confirmButtonText: t("censor.deleteButton"),
            cancelButtonText: t("swal.cancelButton"),
        }).then((result) => {

            /* Check Answer */
            if (!result.isConfirmed) {
                return
            }

            /* Send to REST API */
            censorDelete({ id: item.id }).then((response) => {
                if (response.status === 200) {
                    Swal.fire(utils.notification(t("censor.deleted"), "success"))
                    this.loadCensor()
                }
                else {
                    Swal.fire(utils.notification(this.message(response), "error"))
                }
            }).catch(() => {
                Swal.fire(utils.notification(t("swal.generic"), "error"))
            })

        })
    }

    /* Copy Action — the text of a message goes to the clipboard straight from the row */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            Swal.fire(utils.notification(t("msg.copyFailed"), "error"))
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("msg.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(String(value)).then(() => {
            Swal.fire(utils.notification(t("msg.copied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("msg.copyFailed"), "error"))
        })

    }

    /* Count Of The Active Filters — the badge of the filter button */
    getCount = () => {

        /* Fields */
        const { selected, operators, tables, testers } = this.state
        let count = 0

        if (selected !== "all") {
            count = count + 1
        }

        if (operators.length > 0) {
            count = count + 1
        }

        if (tables.length > 0) {
            count = count + 1
        }

        if (!testers) {
            count = count + 1
        }

        /* Send Result */
        return count
    }

    /* Draw Search */
    _search = () => {

        /* Fields */
        const { search, tab } = this.state

        return (
            <div className="msg--search">

                <SearchIcon />

                <input
                    value={search}
                    onChange={(event) => this.changeSearch(event.target.value)}
                    placeholder={tab === DICTIONARY ? t("censor.search") : t("msg.search")}
                />

                {search !== "" &&
                    <div onClick={() => this.changeSearch("")} className="msg--search-clear">
                        <CloseIcon size={12} />
                    </div>
                }

            </div>
        )
    }

    /* Draw Filter Button — the dictionary is filtered by nothing but its own search */
    _filter = () => {

        /* Fields */
        const { selected, start, end, operators, tables, testers, tab } = this.state
        const count = this.getCount()

        /* Check Tab */
        if (tab === DICTIONARY) {
            return null
        }

        return (
            <div
                onClick={() => this._filterModal.open({ selected, start, end, operators, tables, testers })}
                className={`msg--filter ${count > 0 ? "active" : ""}`}
            >
                <FilterIcon size={15} />
                <span>{t("msg.filter")}</span>
                {count > 0 && <div className="msg--filter-count">{count}</div>}
            </div>
        )
    }

    /* Draw Create Button — a superadmin, a manager and a pitboss write the dictionary, a viewer only reads it */
    _create = () => {

        /* Fields */
        const { tab } = this.state
        const { admin } = this.props

        /* Check Tab */
        if (tab !== DICTIONARY) {
            return null
        }

        /* Check Access */
        if (!utils.can(admin, "edit")) {
            return null
        }

        return (
            <div onClick={() => this._censorForm.open()} className="msg--create">
                <PlusIcon size={15} />
                <span>{t("censor.add")}</span>
            </div>
        )
    }

    /* Draw Tabs — the messages, the ones the censor has caught and the dictionary itself */
    _tabs = () => {

        /* Fields */
        const { tab, counts, censorStats } = this.state

        return (
            <div className="msg--tabs">

                <div onClick={() => this.changeTab("all")} className={`msg--tab ${tab === "all" ? "active" : ""}`}>
                    <ChatIcon size={15} />
                    <span>{t("msg.tab.all")}</span>
                    <div className="msg--tab-count">{formatCount(counts.all)}</div>
                </div>

                <div onClick={() => this.changeTab("censored")} className={`msg--tab ${tab === "censored" ? "active" : ""}`}>
                    <ShieldIcon size={15} />
                    <span>{t("msg.tab.censored")}</span>
                    <div className="msg--tab-count">{formatCount(counts.censored)}</div>
                </div>

                <div onClick={() => this.changeTab(DICTIONARY)} className={`msg--tab ${tab === DICTIONARY ? "active" : ""}`}>
                    <BookIcon size={15} />
                    <span>{t("censor.tab")}</span>
                    <div className="msg--tab-count">{formatCount(censorStats.total)}</div>
                </div>

            </div>
        )
    }

    /* Draw One Number Of The Filter */
    _stat = (icon, title, value, tone = "") => {

        return (
            <div className={`msg--stat ${tone}`}>

                <div className="msg--stat-icon">{icon}</div>

                <div className="msg--stat-text">
                    <div className="msg--stat-title">{title}</div>
                    <div className="msg--stat-value">{value}</div>
                </div>

            </div>
        )
    }

    /* Draw Numbers — what the current filter really holds */
    _numbers = () => {

        /* Fields */
        const { tab, count, counts, stats, censorStats } = this.state

        /* Check Tab — the dictionary counts words, not messages */
        if (tab === DICTIONARY) {
            return (
                <div className="msg--stats">

                    {this._stat(<BookIcon />, t("censor.total"), formatCount(censorStats.total), "green")}
                    {this._stat(<CheckIcon size={13} />, t("censor.active"), formatCount(censorStats.active), "mint")}
                    {this._stat(<PowerIcon size={13} />, t("censor.inactive"), formatCount(censorStats.total - censorStats.active), "amber")}
                    {this._stat(<ShieldIcon size={13} />, t("msg.tab.censored"), formatCount(counts.censored), "gold")}

                </div>
            )
        }

        return (
            <div className="msg--stats">

                {this._stat(<LayersIcon />, t("msg.total"), formatCount(count), "green")}
                {this._stat(<ShieldIcon size={13} />, t("msg.censoredCount"), formatCount(counts.censored), "gold")}
                {this._stat(<PlayerIcon />, t("msg.playersCount"), formatCount(stats.players), "mint")}
                {this._stat(<TableIcon />, t("msg.gamesCount"), formatCount(stats.tables), "amber")}
                {this._stat(<ClockIcon />, t("msg.lastMessage"), stats.last ? formatAgo(stats.last) : "—", "olive")}

            </div>
        )
    }

    /* Draw The Actions Of A Word */
    _wordActions = (item) => {

        /* Fields */
        const { admin } = this.props

        /* Check Access */
        if (!utils.can(admin, "edit")) {
            return <div className="msg--action-empty">—</div>
        }

        return (
            <>

                <div
                    onClick={() => this.toggleWord(item)}
                    className={`msg--action ${parseInt(item.isActive) === 1 ? "green" : ""}`}
                    title={parseInt(item.isActive) === 1 ? t("censor.switchOff") : t("censor.switchOn")}
                >
                    <PowerIcon size={15} />
                </div>

                <div onClick={() => this._censorForm.open(item)} className="msg--action blue" title={t("censor.edit")}>
                    <EditIcon size={15} />
                </div>

                <div onClick={() => this.deleteWord(item)} className="msg--action red" title={t("censor.delete")}>
                    <TrashIcon size={15} />
                </div>

            </>
        )
    }

    /* Draw The Dictionary */
    _dictionary = () => {

        /* Fields */
        const { censorStatus, censor, search } = this.state

        /* Check Status */
        if (censorStatus !== "ok") {
            return (
                <div className="msg--page">
                    <InnerLayout inner status={censorStatus} reload={() => this.loadCensor(true)} />
                </div>
            )
        }

        return (
            <div className="msg--page">

                {/* Hint — what the two types of a word really mean */}
                <div className="msg--note">
                    <ShieldIcon size={15} />
                    <span>{t("censor.note")}</span>
                </div>

                {/* Head */}
                <div className="msg--table-header censor">
                    <div className="msg--column msg--col-number">#</div>
                    <div className="msg--column msg--col-word">{t("censor.word")}</div>
                    <div className="msg--column msg--col-type">{t("censor.type")}</div>
                    <div className="msg--column msg--col-state">{t("censor.status")}</div>
                    <div className="msg--column msg--col-comment">{t("censor.comment")}</div>
                    <div className="msg--column msg--col-author">{t("censor.author")}</div>
                    <div className="msg--column msg--col-actions" />
                </div>

                {/* Empty */}
                {censor.length === 0 &&
                    <div className="msg--empty">
                        <BookIcon size={40} />
                        <span>{search !== "" ? t("msg.nothingFound") : t("censor.empty")}</span>
                    </div>
                }

                {/* Body */}
                {censor.map((item, index) =>
                    <div className={`msg--row censor ${parseInt(item.isActive) === 1 ? "" : "off"}`} key={`${index}`}>

                        {/* Number */}
                        <div className="msg--column msg--col-number">{index + 1}</div>

                        {/* Word */}
                        <div className="msg--column msg--col-word">
                            <div className="msg--word">{item.word}</div>
                        </div>

                        {/* Type */}
                        <div className="msg--column msg--col-type">
                            <div className={`msg--type ${item.type === "exact" ? "exact" : "root"}`}>
                                {item.type === "exact" ? t("censor.type.exact") : t("censor.type.root")}
                            </div>
                        </div>

                        {/* Status */}
                        <div className="msg--column msg--col-state">
                            <div className={`msg--state ${parseInt(item.isActive) === 1 ? "on" : "off"}`}>
                                {parseInt(item.isActive) === 1 ? t("censor.on") : t("censor.off")}
                            </div>
                        </div>

                        {/* Comment */}
                        <div className="msg--column msg--col-comment">
                            <span title={utils.checkValue(item.comment, "")}>{utils.checkValue(item.comment, "—")}</span>
                        </div>

                        {/* Author */}
                        <div className="msg--column msg--col-author">

                            <div className="msg--author">
                                <AdminIcon size={12} />
                                <span>{utils.checkValue(item.admin, "—")}</span>
                            </div>

                            <div className="msg--author-note">{formatDay(item.createdAt)}</div>

                        </div>

                        {/* Actions */}
                        <div className="msg--column msg--col-actions">{this._wordActions(item)}</div>

                    </div>
                )}

            </div>
        )
    }

    /* Draw Content */
    _content = () => {

        /* Fields */
        const { status, data, count, search, tab } = this.state
        const { navigate } = this.props
        const page = this.getPage()

        /* Check Tab */
        if (tab === DICTIONARY) {
            return this._dictionary()
        }

        /* Check Status */
        if (status !== "ok") {
            return (
                <div className="msg--page">
                    <InnerLayout inner status={status} reload={() => this.load(true)} />
                </div>
            )
        }

        return (
            <div className="msg--page">

                {/* Head */}
                <div className="msg--table-header">
                    <div className="msg--column msg--col-number">#</div>
                    <div className="msg--column msg--col-player">{t("msg.player")}</div>
                    <div className="msg--column msg--col-game">{t("msg.game")}</div>
                    <div className="msg--column msg--col-operator">{t("msg.operator")}</div>
                    <div className="msg--column msg--col-text">{t("msg.text")}</div>
                    <div className="msg--column msg--col-dealer">{t("msg.dealer")}</div>
                    <div className="msg--column msg--col-date">{t("msg.date")}</div>
                    <div className="msg--column msg--col-actions" />
                </div>

                {/* Empty */}
                {data.length === 0 &&
                    <div className="msg--empty">
                        <ChatIcon size={40} />
                        <span>
                            {search !== "" || this.getCount() > 0
                                ? t("msg.nothingFound")
                                : tab === "censored" ? t("msg.emptyCensored") : t("msg.empty")
                            }
                        </span>
                    </div>
                }

                {/* Body */}
                {data.map((item, index) =>
                    <div onClick={() => this._playerView.open({ playerid: item.player, ava: getAvaIndex(item) })} className="msg--row" key={`${index}`}>

                        {/* Number */}
                        <div className="msg--column msg--col-number">{(page - 1) * SIZE + index + 1}</div>

                        {/* Player */}
                        <div className="msg--column msg--col-player">

                            <Avatar uri={getAvatar(item)} size={38} />

                            <div className="msg--player-text">
                                <div className="msg--player-name">
                                    <span className="msg--player-value">{getName(item)}</span>
                                    {parseInt(item.isTester) === 1 && <span className="msg--tag tester">{t("msg.tester")}</span>}
                                    {parseInt(item.isVip) === 1 &&
                                        <span className="msg--tag vip">
                                            <VipIcon size={10} />
                                            VIP
                                        </span>
                                    }
                                </div>
                                <div className="msg--player-code">{item.player}</div>
                            </div>

                        </div>

                        {/* Game */}
                        <div className="msg--column msg--col-game">

                            <div className="msg--game-name">{getGame(item)}</div>

                            <div className="msg--game-note">
                                <StudioIcon size={11} />
                                {utils.checkValue(item.studio, "—")}
                            </div>

                        </div>

                        {/* Operator */}
                        <div className="msg--column msg--col-operator">
                            <div className="msg--operator" title={utils.checkValue(item.operator, "")}>
                                <OperatorIcon size={12} />
                                <span>{utils.checkValue(item.operator, "—")}</span>
                            </div>
                        </div>

                        {/* Message — the row always keeps the text as it was typed, the table has read stars */}
                        <div className="msg--column msg--col-text">

                            <div className={`msg--bubble ${parseInt(item.isCensored) === 1 ? "censored" : ""}`}>{item.message}</div>

                            {parseInt(item.isCensored) === 1 &&
                                <div className="msg--censored" title={t("msg.censoredHint")}>
                                    <ShieldIcon size={11} />
                                    {t("msg.censored")}
                                </div>
                            }

                            {item.gameNumber &&
                                <div className="msg--number">
                                    <HashIcon size={11} />
                                    {item.gameNumber}
                                </div>
                            }

                        </div>

                        {/* Dealer */}
                        <div className="msg--column msg--col-dealer">
                            {item.dealer
                                ? <div className="msg--dealer">
                                    <DealerIcon size={12} />
                                    {item.dealer}
                                </div>
                                : <div className="msg--dealer empty">—</div>
                            }
                        </div>

                        {/* Date */}
                        <div className="msg--column msg--col-date">
                            <div className="msg--date">{formatDay(item.createdAt)}</div>
                            <div className="msg--time">{formatTime(item.createdAt)}</div>
                            <div className="msg--ago">{formatAgo(item.createdAt)}</div>
                        </div>

                        {/* Actions */}
                        <div onClick={(event) => event.stopPropagation()} className="msg--column msg--col-actions">

                            <div onClick={() => this._playerView.open({ playerid: item.player, ava: getAvaIndex(item) })} className="msg--action" title={t("msg.openPlayer")}>
                                <PlayerIcon size={15} />
                            </div>

                            <div onClick={() => this.copy(item.message)} className="msg--action" title={t("msg.copyText")}>
                                <CopyIcon size={15} />
                            </div>

                        </div>

                    </div>
                )}

                {/* Pagination */}
                <Pagination size={SIZE} page={page} total={count} onPageChange={(page) => navigate(`/message/${page}`)} />

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { status, tab, censorStatus } = this.state

        /* The tools of the open tab only make sense when the tab itself has really loaded */
        const ready = tab === DICTIONARY ? censorStatus === "ok" : status === "ok"

        return (
            <div className="page">

                <div className="page-header msg--header">

                    <h1>{t("msg.title")}</h1>

                    <div className="msg--tools">

                        {/* Draw Search */}
                        {ready && this._search()}

                        {/* Draw Filter Button */}
                        {ready && this._filter()}

                        {/* Draw Create Button */}
                        {this._create()}

                    </div>

                </div>

                {/* Draw Tabs */}
                {this._tabs()}

                {/* Draw Numbers */}
                {ready && this._numbers()}

                {/* Draw Content */}
                {this._content()}

                {/* Draw Player View Modal */}
                <PlayerView ref={(ref) => this._playerView = ref} />

                {/* Draw Filter Modal */}
                <Filter apply={this.applyFilter} ref={(ref) => this._filterModal = ref} />

                {/* Draw Censor Form Modal */}
                <CensorForm reload={() => this.loadCensor()} ref={(ref) => this._censorForm = ref} />

            </div>
        )
    }

}

const mapStateToProps = (state) => ({
    admin: state.admin,
    language: state.language,
})

export default connect(mapStateToProps)(withRouter(Messages))
